import React, { ChangeEvent, useCallback, useState } from "react";
import { FieldProps } from "formik";
import { TimePicker } from "@mui/x-date-pickers";
import TextInput from "../ui/TextInput";
import Dropdown from "./Dropdown";
import NumberInput from "./NumberInput";
import RadioButton from "./RadioButton";
import { DatePicker } from "@mui/x-date-pickers";
import { TextFieldProps as MUITextFieldProps } from "@mui/material";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { useTranslation } from "react-i18next";
import { uploadFileToS3Complete } from "../../api/s3";

export const theme = createTheme({
    typography: {
        fontFamily: "Hanken Grotesk, Readex Pro, system-ui, sans-serif",
    },
    components: {
        // TextField wrapper
        MuiTextField: {
            styleOverrides: {
                root: {
                    width: 220,
                    "&.text-input-medium": {
                        width: 220,
                    },
                    "&.customDatePicker": {
                        width: 280,
                    },
                },
            },
        },

        // Outlined Input (used by TextField, DatePicker, TimePicker)
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    width: 220,
                    minHeight: 46,
                    fontSize: 15,
                    fontFamily: "Hanken Grotesk, Readex Pro, system-ui, sans-serif",
                    borderRadius: 7,
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: 7,
                        borderColor: "var(--line)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--line)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--green-500)",
                        borderWidth: 1,
                    },
                    "&.Mui-focused": {
                        boxShadow: "0 0 0 3px var(--green-100)",
                    },
                    "& input": {
                        padding: "10px 14px",
                        fontSize: 15,
                        height: "100%",
                        fontFamily: "inherit",
                    },
                    "& input::placeholder": {
                        fontSize: 14,
                        opacity: 0.6,
                        color: "var(--ink-4)",
                    },
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ff0000",
                    },
                    "&.customDatePicker": {
                        width: 280,
                        minHeight: 46,
                    },
                },
            },
        },

        // Base font size
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: 15,
                    fontFamily: "Hanken Grotesk, Readex Pro, system-ui, sans-serif",
                },
            },
        },

        // Form label (normal + floating)
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                    fontFamily: "Hanken Grotesk, Readex Pro, system-ui, sans-serif",
                    // Must stay above OutlinedInput (opaque bg + border); select menus are portaled higher
                    zIndex: 1,
                    transform: "translate(14px, 14px) scale(1)",
                    "&.MuiInputLabel-shrink": {
                        fontSize: 11,
                        transform: "translate(14px, -6px) scale(0.95)",
                        // Sit cleanly on the outline notch (background masks the border line)
                        backgroundColor: "var(--surface, #fff)",
                        padding: "0 4px",
                    },
                    "&.Mui-error": {
                        color: "#ff0000",
                    },
                },
            },
        },

        // Error helper text
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: 11,
                    marginTop: 4,
                    "&.Mui-error": {
                        color: "#ff0000",
                        fontSize: 12,
                        margin: "8px 4px 0 4px",
                    },
                },
            },
        },
    },
});

// Common props for all field component
interface BaseFieldProps {
    name: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

// TextField component is a wrapper around TextInput to handle Formik states
interface TextFieldProps {
    placeholder?: string;
    type?: "text" | "email" | "password";
    size?: "small" | "medium" | "big" | "big-with-border";
    disabled?: boolean;
    style?: React.CSSProperties;
    onChange?: any;
    onBlur?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    numberOfLines?: number;
    /** Extra class on the underlying TextInput (e.g. text-input-fluid). */
    customClassName?: string;
    fullWidth?: boolean;
}

export const TextField: React.FC<FieldProps & TextFieldProps> = ({
    field,
    form,
    meta,
    placeholder,
    type = "text",
    size = "small",
    disabled,
    style,
    onChange,
    onBlur,
    numberOfLines,
    customClassName,
    fullWidth = true,
}) => {
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = e.target.value;
            form.setFieldValue(field.name, value);
            if (onChange) {
                onChange(e);
            }
        },
        [field.name, form, onChange],
    );

    const handleBlur = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            form.setFieldTouched(field.name, true);
            // Create a synthetic event for Formik's onBlur
            const blurEvent = {
                ...e,
                target: e.target,
                currentTarget: e.currentTarget,
            } as any;
            field.onBlur(blurEvent);
            if (onBlur) {
                onBlur(e);
            }
        },
        // field.onBlur is the relevant slice of field
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [field.name, form, field.onBlur, onBlur],
    );

    return (
        <div style={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
            <TextInput
                {...field}
                onChange={handleChange}
                onBlur={handleBlur}
                // Prevent from long key press to avoid fast re-renders
                onKeyDown={(e) => {
                    if (e.repeat) {
                        e.preventDefault();
                    }
                }}
                placeholder={placeholder}
                type={type}
                size={size}
                disabled={disabled}
                isMissing={hasError}
                numberOfLines={numberOfLines}
                customClassName={customClassName}
                fullWidth={fullWidth}
                style={style}
            />
        </div>
    );
};

// TextAreaField component for multi-line text input
interface TextAreaFieldProps {
    placeholder?: string;
    rows?: number;
    disabled?: boolean;
    style?: React.CSSProperties;
    onChange?: any;
}

export const TextAreaField: React.FC<FieldProps & TextAreaFieldProps> = ({
    field,
    form,
    placeholder,
    rows = 4,
    disabled,
    style,
    onChange,
}) => {
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const value = e.target.value;
            form.setFieldValue(field.name, value);
            if (onChange) {
                onChange(e);
            }
        },
        [field.name, form, onChange],
    );

    const handleBlur = useCallback(() => {
        form.setFieldTouched(field.name, true);
    }, [field.name, form]);

    return (
        <div>
            <textarea
                {...field}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                rows={rows}
                disabled={disabled}
                style={{
                    width: "80%",
                    borderRadius: "11px",
                    border: hasError ? "1px solid #ff0000" : "1px solid #ccc",
                    padding: "10px 14px",
                    fontSize: "12px",
                    fontFamily: "inherit",
                    resize: "vertical",
                    minHeight: `${rows * 20}px`,
                    ...style,
                }}
            />
        </div>
    );
};

// DropdownField component is a wrapper around Dropdown to handle Formik states
interface DropdownFieldProps extends BaseFieldProps {
    options: { value: string; label: string; isDisabled?: boolean }[];
    loading?: boolean;
    label?: string;
    onChange?: (value?: string) => void;
    fullWidth?: boolean;
    maxWidth?: "form" | "none";
    isWithoutMargin?: boolean;
    isClearable?: boolean;
}

export const DropdownField: React.FC<FieldProps & DropdownFieldProps> = ({
    field,
    form,
    name,
    placeholder,
    options,
    loading = false,
    disabled,
    label = "",
    onChange,
    ...props
}) => {
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    const handleChange = useCallback(
        (value: string) => {
            onChange?.(value);
            form.setFieldValue(field.name, value);
        },
        [field.name, form, onChange],
    );

    const handleBlur = useCallback(
        (e: any) => {
            let newEvent = e;
            if (e && e.target && e.target.name !== field.name) {
                const newTarget = e.target.cloneNode(); // make sure we do not modify the real html input
                newTarget.name = field.name;
                newEvent = {
                    ...e,
                    target: newTarget,
                };
            }

            field.onBlur(newEvent);
        },
        [field],
    );

    // Ensure value is always a string (not undefined or null) to avoid React warnings
    const safeValue = field.value ?? "";

    return (
        <Dropdown
            {...field}
            {...props}
            value={safeValue}
            onBlur={handleBlur}
            onChange={handleChange}
            options={options}
            placeholder={placeholder}
            loading={loading}
            error={false}
            isMissing={hasError}
            disabled={disabled}
            label={label}
            name={field.name}
        />
    );
};

// NumberField component is a wrapper around NumberInput to handle Formik states
interface NumberFieldProps extends BaseFieldProps {
    min?: number;
    max?: number;
    fullWidth?: boolean;
}

export const NumberField: React.FC<FieldProps & NumberFieldProps> = ({
    name,
    placeholder,
    min,
    max,
    disabled,
    field,
    form,
    ...props
}) => {
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = parseInt(event.target.value) || 0;
            form.setFieldValue(field.name, value);
        },
        [field.name, form],
    );

    const handleBlur = useCallback(
        (e: any) => {
            let newEvent = e;
            if (e && e.target && e.target.name !== field.name) {
                const newTarget = e.target.cloneNode(); // make sure we do not modify the real html input
                newTarget.name = field.name;
                newEvent = {
                    ...e,
                    target: newTarget,
                };
            }

            field.onBlur(newEvent);
        },
        [field],
    );

    return (
        <NumberInput
            {...field}
            {...props}
            value={field.value || 0}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            hasError={hasError}
            min={min}
            max={max}
        />
    );
};

// RadioField Component is a wrapper around RadioButton to handle Formik states
interface RadioFieldProps extends BaseFieldProps {
    value: string;
    label: string;
    subtitle?: string;
    activeImage?: string;
    inactiveImage?: string;
    tooltip?: string;
}

export const RadioField: React.FC<FieldProps & RadioFieldProps> = ({
    name,
    value,
    label,
    subtitle,
    disabled,
    activeImage,
    inactiveImage,
    tooltip,
    field,
    form,
    ...props
}) => {
    const handleChange = useCallback(() => {
        form.setFieldValue(field.name, value);
        form.setTouched({ ...form.touched, [field.name]: true });
    }, [field.name, form, value]);

    return (
        <RadioButton
            {...field}
            checked={field.value === value}
            onChange={handleChange}
            label={label}
            subtitle={subtitle}
            disabled={disabled}
            activeImage={activeImage}
            inactiveImage={inactiveImage}
            tooltip={tooltip}
            {...props}
        />
    );
};

interface TimePickerFieldProps {
    name: string;
    label?: string;
    placeholder?: string;
    className?: string;
    minTime?: Date | null;
    errorMessage?: string;
}

export const TimePickerField: React.FC<FieldProps & TimePickerFieldProps> = ({
    name,
    label,
    placeholder,
    className = "customDatePicker",
    minTime,
    errorMessage,
    field,
    form,
}) => {
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    return (
        <ThemeProvider theme={theme}>
            <TimePicker
                label={label}
                value={field.value || null}
                onChange={(newValue) => {
                    form.setFieldValue(field.name, newValue, false); // don't validate on every change
                }}
                onAccept={(newValue) => {
                    form.setFieldValue(field.name, newValue, true); // validate when date is picked
                }}
                minTime={minTime}
                slotProps={{
                    textField: {
                        className,
                        placeholder,
                        error: hasError,
                        helperText: hasError ? errorMessage || form.errors[field.name] : "",
                        onBlur: field.onBlur, // Validate on blur
                        name: field.name, // Needed so Formik tracks touched state
                    } as MUITextFieldProps,
                }}
            />
        </ThemeProvider>
    );
};

interface DatePickerFieldProps {
    name: string;
    label?: string;
    placeholder?: string;
    className?: string;
    minDate?: Date | null;
    maxDate?: Date | null;
    errorMessage?: string;
}

export const DatePickerField: React.FC<FieldProps & DatePickerFieldProps> = ({
    name,
    label,
    placeholder,
    className = "customDatePicker",
    minDate,
    maxDate,
    errorMessage,
    field,
    form,
}) => {
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    return (
        <ThemeProvider theme={theme}>
            <DatePicker
                label={label}
                value={field.value || null}
                onChange={(newValue) => {
                    form.setFieldValue(field.name, newValue, false); // don't validate on every change
                }}
                onAccept={(newValue) => {
                    form.setFieldValue(field.name, newValue, true); // validate when date is picked
                }}
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{
                    textField: {
                        className,
                        placeholder,
                        error: hasError,
                        helperText: hasError ? errorMessage || form.errors[field.name] : "",
                        onBlur: field.onBlur, // Validate on blur
                        name: field.name, // Needed so Formik tracks touched state
                    } as MUITextFieldProps,
                }}
            />
        </ThemeProvider>
    );
};

interface MultiSelectDropdownFieldProps {
    name: string;
    label: string;
    placeholder?: string;
    disabled: boolean;
    options: { value: string; label: string }[];
    loading?: boolean;
    fullWidth?: boolean;
    maxWidth?: "form" | "none";
    isWithoutMargin?: boolean;
    className?: string;
}

export const MultiSelectDropdownField: React.FC<FieldProps & MultiSelectDropdownFieldProps> = ({
    field,
    form,
    label,
    placeholder,
    disabled = false,
    options,
    loading = false,
    ...props
}) => {
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    const handleChange = useCallback(
        (selectedValues: string[]) => {
            form.setFieldValue(field.name, selectedValues);
        },
        [form, field.name],
    );

    const handleBlur = useCallback(
        (e: any) => {
            let newEvent = e;
            if (e && e.target && e.target.name !== field.name) {
                const newTarget = e.target.cloneNode(); // make sure we do not modify the real html input
                newTarget.name = field.name;
                newEvent = {
                    ...e,
                    target: newTarget,
                };
            }

            field.onBlur(newEvent);
        },
        [field],
    );

    return (
        <MultiSelectDropdown
            {...field}
            label={label}
            placeholder={placeholder}
            options={options}
            loading={loading}
            disabled={disabled}
            value={field.value || []}
            onChange={handleChange}
            isMissing={hasError}
            onBlur={handleBlur}
            error={false}
            {...props}
        />
    );
};

interface SingleAudioUploaderFieldProps {
    label?: string;
    disabled?: boolean;
}

export const SingleAudioUploaderField: React.FC<FieldProps & SingleAudioUploaderFieldProps> = ({
    field,
    form,
    label,
    disabled = false,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const error = form.touched[field.name] && form.errors[field.name];
    const hasError = Boolean(error);

    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleUpload = useCallback(
        async (file: File) => {
            setIsUploading(true);
            setErrorMessage(null);

            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    throw new Error("No authentication token found");
                }
                const url = await uploadFileToS3Complete(
                    {
                        file,
                        isPublic: true, // Public files for general uploads
                        isFolder: false, // No folder structure for general uploads
                    },
                    token,
                );
                form.setFieldValue(field.name, url);
            } catch (err) {
                console.error(err);
                setErrorMessage(t("errorUploadingAudioFile"));
            } finally {
                setIsUploading(false);
            }
        },
        [field.name, form, t],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (disabled || isUploading) return;

            const files = Array.from(e.dataTransfer.files);
            if (files.length > 1) {
                setErrorMessage(t("multipleFilesNotSupported"));
                return;
            }

            const file = files[0];
            if (!file || !(file.type === "audio/mpeg" || file.name.endsWith(".mp3"))) {
                setErrorMessage(t("onlyMP3AudioFilesSupported"));
                return;
            }

            handleUpload(file);
        },
        [disabled, isUploading, handleUpload, t],
    );

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div className="audio-uploader-wrapper">
            {label && <label className="form-label">{label}</label>}
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`audio-uploader-zone ${isUploading ? "uploading" : ""}`}
                style={{
                    border: hasError ? "2px dashed red" : "2px dashed #ccc",
                    borderRadius: "8px",
                    padding: "20px",
                    textAlign: "center",
                    backgroundColor: disabled ? "#f5f5f5" : "#fff",
                    cursor: disabled ? "not-allowed" : "pointer",
                }}
            >
                {isUploading ? t("uploadingFile") + "..." : t("dragAndDropMP3HereOrClickToBrowse")}

                {field.value && !isUploading && (
                    <div style={{ marginTop: 10 }}>
                        ✅ {t("fileUploaded")}:{" "}
                        <a href={field.value} target="_blank" rel="noreferrer">
                            {field.value}
                        </a>
                    </div>
                )}

                {hasError && typeof error === "string" && <div style={{ color: "red", marginTop: 5 }}>{error}</div>}

                {errorMessage && <div style={{ color: "red", marginTop: 5 }}>{errorMessage}</div>}
            </div>
        </div>
    );
};
